import React, { useRef, useState, forwardRef, useImperativeHandle } from 'react';
import { Button, Menu, MenuItem, ListItemIcon, ListItemText, useMediaQuery } from '@mui/material';
import CameraAltIcon from '@mui/icons-material/CameraAlt';
import PhotoLibraryIcon from '@mui/icons-material/PhotoLibrary';
import { useTranslation } from 'react-i18next';

/**
 * Image picker button that shows camera/gallery options on mobile.
 *
 * On desktop: clicking the button opens the file picker directly.
 * On mobile: clicking the button opens a menu with "Take photo" (camera)
 * and "Choose from gallery" options.
 *
 * Props:
 *   onChange       - file input onChange handler (receives native event)
 *   accept         - accepted MIME types (default: "image/*")
 *   multiple       - allow multiple file selection (default: false)
 *   disabled       - disable the button
 *   buttonLabel    - button text
 *   buttonProps    - additional props spread onto the MUI Button
 */
const ImagePickerButton = forwardRef(function ImagePickerButton(
  { onChange, accept = 'image/*', multiple = false, disabled = false, buttonLabel, buttonProps = {} },
  ref
) {
  const { t } = useTranslation();
  const isMobile = useMediaQuery('(pointer: coarse)');
  const [menuAnchor, setMenuAnchor] = useState(null);

  const galleryInputRef = useRef(null);
  const cameraInputRef = useRef(null);

  // Expose reset method so parents can clear the input value
  useImperativeHandle(ref, () => ({
    get value() {
      return galleryInputRef.current?.value;
    },
    set value(val) {
      if (galleryInputRef.current) galleryInputRef.current.value = val;
      if (cameraInputRef.current) cameraInputRef.current.value = val;
    },
  }));

  const handleChange = (event) => {
    if (onChange) onChange(event);
    // Reset inputs so selecting the same file again triggers onChange
    if (galleryInputRef.current) galleryInputRef.current.value = '';
    if (cameraInputRef.current) cameraInputRef.current.value = '';
  };

  const handleDesktopClick = (event) => {
    if (isMobile) {
      setMenuAnchor(event.currentTarget);
    } else {
      galleryInputRef.current?.click();
    }
  };

  const handleCameraClick = () => {
    cameraInputRef.current?.click();
    // Close menu after a tick to avoid interfering with the file dialog on mobile
    setTimeout(() => setMenuAnchor(null), 100);
  };

  const handleGalleryClick = () => {
    galleryInputRef.current?.click();
    setTimeout(() => setMenuAnchor(null), 100);
  };

  return (
    <>
      <Button disabled={disabled} onClick={handleDesktopClick} {...buttonProps}>
        {buttonLabel}
      </Button>

      {/* Gallery file input (desktop + mobile gallery) */}
      <input
        ref={galleryInputRef}
        type="file"
        accept={accept}
        multiple={multiple}
        hidden
        onChange={handleChange}
      />

      {/* Camera file input (mobile only) */}
      {isMobile && (
        <input
          ref={cameraInputRef}
          type="file"
          accept="image/*"
          capture="environment"
          hidden
          onChange={handleChange}
        />
      )}

      {/* Mobile menu */}
      <Menu
        anchorEl={menuAnchor}
        open={Boolean(menuAnchor)}
        onClose={() => setMenuAnchor(null)}
      >
        <MenuItem onClick={handleCameraClick}>
          <ListItemIcon>
            <CameraAltIcon fontSize="small" />
          </ListItemIcon>
          <ListItemText>{t('Take photo')}</ListItemText>
        </MenuItem>
        <MenuItem onClick={handleGalleryClick}>
          <ListItemIcon>
            <PhotoLibraryIcon fontSize="small" />
          </ListItemIcon>
          <ListItemText>{t('Choose from gallery')}</ListItemText>
        </MenuItem>
      </Menu>
    </>
  );
});

export default ImagePickerButton;
